import React, { useEffect, useState, useCallback } from 'react';
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody as ChakraModalBody,
  ModalCloseButton,
  ModalContent as ChakraModalContent,
  ModalFooter as ChakraModalFooter,
  ModalHeader as ChakraModalHeader,
  ModalOverlay as ChakraModalOverlay,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { DeleteIcon, DownloadIcon } from '@chakra-ui/icons';
import { FiFileText } from 'react-icons/fi';
import { useAuth } from 'contexts/AuthContext';
import { useGetUserRole } from 'domains/user/hooks/useGetUserRole';
import { useTranslation } from 'i18n/TranslationProvider';
import API from 'api/api';
import { useUnifiedTableStyles } from 'components/layout/useUnifiedUiStyles';

const getStatusColor = (status, isInvoice) => {
  if (isInvoice) return status === 'paid' ? 'green' : 'red';
  if (status === 'confirmed') return 'green';
  if (status === 'pending') return 'orange';
  if (status === 'expired') return 'gray';
  return 'gray';
};

const toDateLabel = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
};
const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const formatStatusLabel = (status, t) => {
  if (!status) return t('auth.status_label');
  const normalized = String(status).toLowerCase();
  // Check if we have a translation for this status
  const translation = t(`validationStatus_${normalized}`, { defaultValue: null });
  if (translation) return translation;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatListingRef = (listingId) => {
  if (!listingId) return '-';
  const value = String(listingId);
  if (value.length <= 10) return value;
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
};

const getPayoutDate = (doc) => doc?.paidAt || doc?.payoutDate || null;

const DocumentList = ({
  entityType,
  entityId,
  docType,
  isInvoice = false,
  invoiceListingId,
  invoiceBookingId,
  invoiceStatus = 'all',
  invoiceSearch = '',
  paginated = false,
  page = 1,
  limit = 20,
  onMetaChange,
  refreshTrigger = 0,
  canDelete = false,
  canMarkPaid = false,
  currentRole = null,
  resolveRole = true,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { role: resolvedRole } = useGetUserRole({ enabled: resolveRole });
  const effectiveRole = currentRole || resolvedRole;
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isMarkPaidOpen,
    onOpen: onMarkPaidOpen,
    onClose: onMarkPaidClose,
  } = useDisclosure();
  const cancelRef = React.useRef();
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [invoiceToMarkPaid, setInvoiceToMarkPaid] = useState(null);
  const [paidAtDate, setPaidAtDate] = useState('');

  const panelBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  const cardBg = useColorModeValue('white', 'whiteAlpha.80');
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const strongText = useColorModeValue('navy.700', 'white');
  const { tableSx } = useUnifiedTableStyles();

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      let endpoint = '/documents';
      let params = {};

      if (isInvoice) {
        endpoint = '/invoices';
        if (entityId) params.uploadedBy = entityId;
        if (invoiceListingId || invoiceBookingId) params.listingId = invoiceListingId || invoiceBookingId;
        if (invoiceStatus && invoiceStatus !== 'all') {
          params.status = invoiceStatus;
        }
        if (invoiceSearch) {
          params.search = invoiceSearch;
        }
        if (paginated) {
          params.paginated = 'true';
          params.page = page;
          params.limit = limit;
        }
      } else {
        params = { entityType, entityId };
        if (docType) params.docType = docType;
      }

      const response = await API.get(endpoint, { params });
      const payload = response.data.data || [];
      if (isInvoice && !Array.isArray(payload)) {
        onMetaChange?.(payload.meta || null);
      } else {
        onMetaChange?.(null);
      }
      let docs = Array.isArray(payload) ? payload : payload.items || [];
      docs = docs.map((doc) => ({
        ...doc,
        id: doc.id || doc.documentId || doc._id,
      }));

      if (!isInvoice && docType) {
        docs = docs.filter((d) => d.docType === docType);
      }

      setDocuments(docs);
    } catch (error) {
      console.error('Failed to fetch documents', error);
      toast({
        title: t('Error loading documents'),
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [
    entityType,
    entityId,
    docType,
    isInvoice,
    invoiceBookingId,
    invoiceListingId,
    invoiceStatus,
    invoiceSearch,
    paginated,
    page,
    limit,
    onMetaChange,
    t,
    toast,
  ]);

  useEffect(() => {
    if (entityId || isInvoice) {
      fetchDocuments();
    }
  }, [fetchDocuments, refreshTrigger, entityId, isInvoice]);

  const confirmDelete = (id) => {
    setDocumentToDelete(id);
    onDeleteOpen();
  };

  const openMarkPaidDialog = (invoice) => {
    setInvoiceToMarkPaid(invoice);
    setPaidAtDate(toDateInputValue(getPayoutDate(invoice)) || new Date().toISOString().slice(0, 10));
    onMarkPaidOpen();
  };
  const closeMarkPaidDialog = () => {
    setInvoiceToMarkPaid(null);
    onMarkPaidClose();
  };

  const handleDelete = async () => {
    if (!documentToDelete) return;
    try {
      const endpoint = isInvoice
        ? `/invoices/${documentToDelete}`
        : `/documents/${documentToDelete}`;
      await API.delete(endpoint);
      toast({ title: t('Document deleted'), status: 'success' });
      fetchDocuments();
    } catch (error) {
      toast({ title: t('Failed to delete'), status: 'error' });
    } finally {
      onDeleteClose();
      setDocumentToDelete(null);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const endpoint = isInvoice ? `/invoices/${doc.id}` : `/documents/${doc.id}`;
      const res = await API.get(endpoint);
      const downloadUrl = res.data.data.downloadUrl;
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      } else {
        toast({ title: t('Failed to get download link'), status: 'error' });
      }
    } catch (e) {
      toast({ title: t('Failed to get download link'), status: 'error' });
    }
  };

  const handleTogglePaid = async (doc) => {
    if (doc.status !== 'paid') {
      openMarkPaidDialog(doc);
      return;
    }

    try {
      await API.patch(`/invoices/${doc.id}/status`, { status: 'unpaid' });
      toast({ title: t('Status updated'), status: 'success' });
      fetchDocuments();
    } catch (e) {
      toast({ title: t('Update failed'), status: 'error' });
    }
  };

  const handleConfirmMarkPaid = async () => {
    if (!invoiceToMarkPaid) return;
    if (!paidAtDate) {
      toast({ title: t('Select paid date'), status: 'warning' });
      return;
    }

    try {
      await API.patch(`/invoices/${invoiceToMarkPaid.id}/status`, {
        status: 'paid',
        paidAt: paidAtDate,
      });
      toast({ title: t('Status updated'), status: 'success' });
      fetchDocuments();
      closeMarkPaidDialog();
    } catch (e) {
      toast({
        title: t('Update failed'),
        description: e?.response?.data?.message || e?.message || t('Unexpected error'),
        status: 'error',
      });
    }
  };

  const renderActions = (doc) => (
    <HStack spacing={2}>
      <Tooltip label={t('Download')}>
        <IconButton
          icon={<DownloadIcon />}
          size="xs"
          aria-label={t('Download')}
          onClick={() => handleDownload(doc)}
        />
      </Tooltip>

      {canDelete &&
        ((isInvoice && effectiveRole === 'admin') ||
          (!isInvoice && (effectiveRole === 'admin' || doc.uploadedBy === user?.uid))) && (
        <Tooltip label={t('Delete')}>
          <IconButton
            icon={<DeleteIcon />}
            size="xs"
            colorScheme="red"
            variant="outline"
            aria-label={t('Delete')}
            onClick={() => confirmDelete(doc.id)}
          />
        </Tooltip>
      )}

      {canMarkPaid && isInvoice && (
        <Button
          size="xs"
          colorScheme={doc.status === 'paid' ? 'orange' : 'green'}
          onClick={() => handleTogglePaid(doc)}
        >
          {doc.status === 'paid' ? t('Mark Unpaid') : t('Mark Paid')}
        </Button>
      )}
    </HStack>
  );

  if (loading && documents.length === 0) {
    return (
      <HStack py={4}>
        <Spinner size="sm" />
        <Text fontSize="sm" color={mutedText}>
          {t('Loading...')}
        </Text>
      </HStack>
    );
  }

  if (documents.length === 0) {
    return (
      <Text fontSize="sm" color={mutedText}>
        {t('auth.no_documents_found')}
      </Text>
    );
  }

  if (isMobile) {
    return (
      <Stack spacing={3}>
        {documents.map((doc) => (
          <Box
            key={doc.id}
            border="1px solid"
            borderColor={panelBorder}
            bg={cardBg}
            borderRadius="14px"
            p={3}
          >
            <HStack justify="space-between" align="flex-start" mb={2}>
              <HStack minW={0} spacing={2}>
                <FiFileText />
                <Box minW={0}>
                  <Text noOfLines={1} fontWeight="700" color={strongText}>
                    {isInvoice ? doc.invoiceNumber : doc.fileName}
                  </Text>
                  {isInvoice && (
                    <Text noOfLines={1} fontSize="xs" color={mutedText}>
                      {doc.fileName || '-'}
                    </Text>
                  )}
                  {isInvoice && (doc.listingId || doc.bookingId) && (
                    <Text noOfLines={1} fontSize="xs" color={mutedText}>
                      {t('Listing')}: {formatListingRef(doc.listingId || doc.bookingId)}
                    </Text>
                  )}
                </Box>
              </HStack>
              <Badge colorScheme={getStatusColor(doc.status, isInvoice)}>
                {formatStatusLabel(doc.status, t)}
              </Badge>
            </HStack>

            <HStack justify="space-between" mb={3}>
              <Text fontSize="xs" color={mutedText}>
                {t('Date')}: {toDateLabel(doc.createdAt || doc.uploadedAt)}
              </Text>
              {isInvoice && (
                <Text fontSize="xs" color={mutedText}>
                  {t('Amount')}: {doc.amount ? `€${doc.amount}` : '-'}
                </Text>
              )}
            </HStack>
            {isInvoice && (
              <Text fontSize="xs" color={mutedText} mb={3}>
                {t('Payout date')}: {toDateLabel(getPayoutDate(doc))}
              </Text>
            )}

            {renderActions(doc)}
          </Box>
        ))}

        <AlertDialog
          isOpen={isDeleteOpen}
          leastDestructiveRef={cancelRef}
          onClose={onDeleteClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent bg={cardBg}>
              <AlertDialogHeader fontSize="lg" fontWeight="bold" color={strongText}>
                {t('Delete')}
              </AlertDialogHeader>

              <AlertDialogBody color={mutedText}>
                {t('Are you sure you want to delete this document?')}
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeleteClose} variant="ghost">
                  {t('Cancel')}
                </Button>
                <Button colorScheme="red" onClick={handleDelete} ml={3}>
                  {t('Delete')}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Stack>
    );
  }

  return (
    <Box overflowX="auto" border="1px solid" borderColor={panelBorder} borderRadius="14px">
      <Table variant="simple" size="sm" sx={tableSx}>
        <Thead>
          <Tr>
            <Th>{isInvoice ? t('Invoice #') : t('File Name')}</Th>
            <Th>{t('Date')}</Th>
            {isInvoice && <Th>{t('Amount')}</Th>}
            {isInvoice && <Th>{t('Payout date')}</Th>}
            <Th>{t('Status')}</Th>
            <Th>{t('Actions')}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {documents.map((doc) => (
            <Tr key={doc.id}>
              <Td>
                {isInvoice ? (
                  <Text fontWeight="bold">{doc.invoiceNumber}</Text>
                ) : (
                  <Text>{doc.fileName}</Text>
                )}
                {isInvoice && (
                  <Text fontSize="xs" color={mutedText}>
                    {doc.fileName || '-'}
                  </Text>
                )}
                {isInvoice && (doc.listingId || doc.bookingId) && (
                  <Text fontSize="xs" color={mutedText}>
                    {t('Listing')}: {formatListingRef(doc.listingId || doc.bookingId)}
                  </Text>
                )}
              </Td>
              <Td>{toDateLabel(doc.createdAt || doc.uploadedAt)}</Td>
              {isInvoice && <Td>{doc.amount ? `€${doc.amount}` : '-'}</Td>}
              {isInvoice && <Td>{toDateLabel(getPayoutDate(doc))}</Td>}
              <Td>
                <Badge colorScheme={getStatusColor(doc.status, isInvoice)}>
                  {formatStatusLabel(doc.status, t)}
                </Badge>
              </Td>
              <Td>{renderActions(doc)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardBg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={strongText}>
              {t('Delete')}
            </AlertDialogHeader>

            <AlertDialogBody color={mutedText}>
              {t('Are you sure you want to delete this document?')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose} variant="ghost">
                {t('Cancel')}
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                {t('Delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal isOpen={isMarkPaidOpen} onClose={closeMarkPaidDialog} isCentered>
        <ChakraModalOverlay />
        <ChakraModalContent bg={cardBg}>
          <ChakraModalHeader color={strongText}>{t('Mark Paid')}</ChakraModalHeader>
          <ModalCloseButton />
          <ChakraModalBody>
            <FormControl isRequired>
              <FormLabel>{t('Paid date')}</FormLabel>
              <Input
                type="date"
                value={paidAtDate}
                onChange={(event) => setPaidAtDate(event.target.value)}
              />
            </FormControl>
          </ChakraModalBody>
          <ChakraModalFooter>
            <Button variant="ghost" onClick={closeMarkPaidDialog}>
              {t('Cancel')}
            </Button>
            <Button colorScheme="green" ml={3} onClick={handleConfirmMarkPaid}>
              {t('Mark Paid')}
            </Button>
          </ChakraModalFooter>
        </ChakraModalContent>
      </Modal>
    </Box>
  );
};

export default DocumentList;
